import React, { Dispatch, ReactElement, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../store/types';
import ReduxActions from '../store/ReduxActions';
import { actionCreators } from '../store/actions';
import { DeliveryMode, ORDER_METHODS, PAYMENT_METHODS, PaymentMethod } from '../services/exports/Constants';
import { PaymentOptionPropertiesInterface } from '../services/exports/Interfaces';
import collect from 'collect.js';

interface Props {
  children: ReactElement;
}

interface PaymentFlowContextInterface {
  loading: boolean;
  hasOnlinePayments: boolean;
  enabledWallets: PaymentMethod[];
  paymentOptions: PaymentOptionPropertiesInterface[];
  selectedPaymentMethod: null | PaymentMethod;
  savedPaymentMethod: null | PaymentOptionPropertiesInterface;
  isDirectPayment: boolean;
  isWalletPayment: boolean;
  shouldSavePaymentInfo: boolean;
  hasSavedPaymentMethod: boolean;
  setEnabledWallets: Dispatch<SetStateAction<PaymentMethod[]>>;
  setValidatedWallets: Dispatch<SetStateAction<PaymentMethod[]>>;
  setSelectedPaymentMethod: (paymentMethod: null | PaymentMethod) => void;
  setShouldSavePaymentInfo: (value: boolean) => void;
  findPaymentMethod: (paymentMethod: PaymentMethod) => PaymentOptionPropertiesInterface;
  findSavedPaymentMethod: (paymentMethod: PaymentMethod) => PaymentOptionPropertiesInterface;
  isPaymentMethodSaved: (paymentMethod: PaymentMethod) => boolean;
  isPaymentMethodDisabled: (paymentMethod: PaymentMethod) => boolean;
}

export const PaymentFlowContext = React.createContext<PaymentFlowContextInterface>(null);

export default function PaymentFlowProvider(props: Props): ReactElement {
  const { children } = props;

  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const { payment_options } = useSelector((state: StoreInterface) => state.paymentOptions);
  const { selectedPaymentMethod } = useSelector((state: StoreInterface) => state.paymentFlow);

  const [enabledWallets, setEnabledWallets] = useState([]);
  const [validatedWallets, setValidatedWallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shouldSavePaymentInfo, setShouldSavePaymentInfo] = useState(true);

  const isDirectPayment =
    selectedPaymentMethod !== PaymentMethod.Paypal && selectedPaymentMethod !== PaymentMethod.ApplePay;

  const setSelectedPaymentMethod = (paymentMethod: null | PaymentMethod) => {
    setLoading(true);
    ReduxActions.dispatch(actionCreators.paymentFlow.setSelectedPaymentMethod(paymentMethod));
    setTimeout(() => setLoading(false), 5);
  };

  const hasOnlinePayments = useMemo(
    () =>
      collect(company?.supported_payment_methods)
        .reject((item) => item === PaymentMethod.Cash)
        .isNotEmpty(),
    [company?.supported_payment_methods],
  );

  const isPaymentOptionSupported = useCallback(
    (paymentOption: PaymentOptionPropertiesInterface) => {
      const hiddenForOrderMethods = company?.hidden_payment_methods
        ? (company?.hidden_payment_methods[paymentOption.payment_method_id] ?? [])
        : [];

      return (
        company?.supported_payment_methods?.includes(paymentOption.payment_method_id) &&
        (paymentOption.payment_method_id !== PaymentMethod.ApplePay ||
          (selectedPaymentMethod === PaymentMethod.ApplePay && !validatedWallets.includes(PaymentMethod.ApplePay)) ||
          enabledWallets.includes(PaymentMethod.ApplePay)) &&
        (paymentOption.payment_method_id !== PaymentMethod.GooglePay ||
          (selectedPaymentMethod === PaymentMethod.GooglePay && !validatedWallets.includes(PaymentMethod.GooglePay)) ||
          enabledWallets.includes(PaymentMethod.GooglePay)) &&
        !hiddenForOrderMethods.includes('*') &&
        !hiddenForOrderMethods.includes(cached_order?.method)
      );
    },
    [
      enabledWallets,
      validatedWallets,
      cached_order?.method,
      selectedPaymentMethod,
      company?.hidden_payment_methods,
      company?.supported_payment_methods,
    ],
  );

  const paymentOptions = useMemo<PaymentOptionPropertiesInterface[]>(
    () =>
      collect(PAYMENT_METHODS())
        .reject((item: PaymentOptionPropertiesInterface) => !isPaymentOptionSupported(item))
        .map((item: PaymentOptionPropertiesInterface) => {
          const savedPaymentItem = collect(payment_options).firstWhere('payment_method_id', item.payment_method_id);

          return {
            ...item,
            ...(savedPaymentItem ?? {}),
            is_saved: !!savedPaymentItem,
            is_default: savedPaymentItem?.is_default === true,
          };
        })
        .toArray(),
    [payment_options, isPaymentOptionSupported],
  );

  const savedPaymentMethod = useMemo(
    () =>
      collect(paymentOptions)
        .sortBy('is_default')
        .first((item) => item.is_saved),
    [paymentOptions],
  );

  const findPaymentMethod = useCallback(
    (paymentMethod: PaymentMethod): PaymentOptionPropertiesInterface =>
      collect(paymentOptions).firstWhere('payment_method_id', paymentMethod),
    [paymentOptions],
  );

  const findSavedPaymentMethod = useCallback(
    (paymentMethod: PaymentMethod): PaymentOptionPropertiesInterface => {
      const result = findPaymentMethod(paymentMethod);

      return result?.is_saved ? result : null;
    },
    [paymentOptions],
  );

  const isPaymentMethodSaved = useCallback(
    (paymentMethod: PaymentMethod): boolean => findSavedPaymentMethod(paymentMethod) !== null,
    [paymentOptions],
  );

  const isPaymentMethodDisabled = useCallback(
    (paymentMethod: PaymentMethod): boolean =>
      paymentMethod === PaymentMethod.Cash &&
      cached_order?.method === ORDER_METHODS.DELIVERY &&
      company?.delivery_mode === DeliveryMode.External,
    [cached_order?.method, company?.delivery_mode],
  );

  useEffect(() => {
    if (
      !selectedPaymentMethod ||
      !findPaymentMethod(selectedPaymentMethod) ||
      isPaymentMethodDisabled(selectedPaymentMethod)
    ) {
      setSelectedPaymentMethod(savedPaymentMethod?.payment_method_id);
    }
  }, [paymentOptions]);

  const context = useMemo(
    () => ({
      loading,
      hasOnlinePayments,
      enabledWallets,
      paymentOptions,
      selectedPaymentMethod,
      savedPaymentMethod,
      isDirectPayment,
      isWalletPayment:
        selectedPaymentMethod === PaymentMethod.ApplePay || selectedPaymentMethod === PaymentMethod.GooglePay,
      shouldSavePaymentInfo,
      hasSavedPaymentMethod: Boolean(savedPaymentMethod),
      setEnabledWallets,
      setValidatedWallets,
      setSelectedPaymentMethod,
      setShouldSavePaymentInfo,
      findPaymentMethod,
      findSavedPaymentMethod,
      isPaymentMethodSaved,
      isPaymentMethodDisabled,
    }),
    [
      loading,
      hasOnlinePayments,
      enabledWallets,
      paymentOptions,
      selectedPaymentMethod,
      savedPaymentMethod,
      isDirectPayment,
      shouldSavePaymentInfo,
      findPaymentMethod,
      findSavedPaymentMethod,
      isPaymentMethodSaved,
      isPaymentMethodDisabled,
    ],
  );

  return <PaymentFlowContext.Provider value={context}>{children}</PaymentFlowContext.Provider>;
}
